import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { adjustAdminInventory, getAdminInventory, type AdminInventoryRow } from "@/lib/admin-api-client";
import { describeAdminError } from "@/components/admin/adminErrors";

const PAGE_SIZE = 20;

/**
 * `/admin/stock` — équivalent de apps/web/app/(admin)/admin/stock/page.tsx :
 * tableau des niveaux de stock. Filtrage (recherche + "stock bas uniquement")
 * et pagination pilotés par les paramètres d'URL (`useSearchParams`,
 * remplace le formulaire GET natif) ; l'ajustement par ligne passe par
 * `useMutation` (remplace `adjustInventoryAction`, Server Action), qui
 * invalide la requête d'inventaire courante au lieu de rediriger vers la même
 * vue filtrée.
 */
export function AdminStockPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const lowStockOnly = searchParams.get("lowStockOnly") === "true";
  const search = searchParams.get("search")?.trim() ?? "";
  const pageParam = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const queryKey = ["admin", "inventory", { lowStockOnly, search, page, pageSize: PAGE_SIZE }] as const;

  const { data: result, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () =>
      getAdminInventory(undefined, {
        lowStockOnly: lowStockOnly || undefined,
        search: search || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const [feedback, setFeedback] = useState<{ type: "error" | "success"; message: string } | null>(null);

  const adjustMutation = useMutation({
    mutationFn: ({
      variantId,
      warehouseId,
      quantityDelta,
      reason,
    }: {
      variantId: string;
      warehouseId: string;
      quantityDelta: number;
      reason: string;
    }) => adjustAdminInventory(undefined, variantId, { warehouseId, quantityDelta, reason }),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Ajustement enregistré." });
      queryClient.invalidateQueries({ queryKey: ["admin", "inventory"] });
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const next = new URLSearchParams();
    const nextSearch = String(formData.get("search") ?? "").trim();
    if (nextSearch) next.set("search", nextSearch);
    if (formData.get("lowStockOnly") === "on") next.set("lowStockOnly", "true");
    setSearchParams(next);
  }

  function handleAdjustSubmit(event: FormEvent<HTMLFormElement>, row: AdminInventoryRow) {
    event.preventDefault();
    setFeedback(null);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const quantityDeltaRaw = String(formData.get("quantityDelta") ?? "").trim();
    const reason = String(formData.get("reason") ?? "").trim();
    const quantityDelta = Number.parseInt(quantityDeltaRaw, 10);

    if (!Number.isInteger(quantityDelta) || quantityDelta === 0 || String(quantityDelta) !== quantityDeltaRaw) {
      setFeedback({ type: "error", message: "Le delta doit être un nombre entier non nul." });
      return;
    }
    if (!reason) {
      setFeedback({ type: "error", message: "Le motif de l'ajustement est obligatoire." });
      return;
    }

    adjustMutation.mutate(
      { variantId: row.variantId, warehouseId: row.warehouseId, quantityDelta, reason },
      { onSuccess: () => formEl.reset() },
    );
  }

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-ink">Stock</h1>
      <p className="mt-1 text-sm text-muted">
        Niveaux de stock par variante et entrepôt. {result ? `${result.totalItems} ligne(s) au total.` : "…"}
      </p>

      {isError && (
        <p className="mt-4 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {describeAdminError(error)}
        </p>
      )}
      {feedback && (
        <p
          className={[
            "mt-4 rounded-sm border px-4 py-3 text-sm",
            feedback.type === "error"
              ? "border-red-300 bg-red-50 text-red-700"
              : "border-green-300 bg-green-50 text-green-700",
          ].join(" ")}
        >
          {feedback.message}
        </p>
      )}

      <form onSubmit={handleFilterSubmit} className="mt-6 flex flex-wrap items-end gap-4 rounded-sm border border-line bg-white p-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="search" className="text-xs font-medium text-muted">
            Recherche (produit, SKU)
          </label>
          <input
            id="search"
            name="search"
            type="text"
            defaultValue={search}
            placeholder="Nom du produit ou SKU…"
            className="w-64 rounded-sm border border-line px-3 py-2 text-sm text-ink"
          />
        </div>

        <label className="flex items-center gap-2 pb-2 text-sm text-ink">
          <input type="checkbox" name="lowStockOnly" defaultChecked={lowStockOnly} />
          Stock bas uniquement
        </label>

        <button type="submit" className="rounded-sm bg-navy px-4 py-2 text-sm font-medium text-cream hover:bg-navy/90">
          Filtrer
        </button>

        {(search || lowStockOnly) && (
          <Link to="/admin/stock" className="pb-2 text-sm font-medium text-navy hover:underline">
            Réinitialiser
          </Link>
        )}
      </form>

      <div className="mt-6 overflow-x-auto rounded-sm border border-line bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3">Produit</th>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Entrepôt</th>
              <th className="px-4 py-3 text-right">En stock</th>
              <th className="px-4 py-3 text-right">Réservé</th>
              <th className="px-4 py-3 text-right">Disponible</th>
              <th className="px-4 py-3 text-right">Seuil bas</th>
              <th className="px-4 py-3">Ajustement</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !result && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted">
                  Chargement…
                </td>
              </tr>
            )}
            {result && result.items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted">
                  Aucune ligne de stock ne correspond à ces critères.
                </td>
              </tr>
            )}
            {result?.items.map((row) => (
              <StockRow
                key={row.inventoryId}
                row={row}
                onSubmit={handleAdjustSubmit}
                disabled={adjustMutation.isPending}
              />
            ))}
          </tbody>
        </table>
      </div>

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3 text-sm">
          <PageLink
            page={page - 1}
            disabled={page <= 1}
            lowStockOnly={lowStockOnly}
            search={search}
            label="← Précédent"
          />
          <span className="text-muted">
            Page {result.page} / {result.totalPages}
          </span>
          <PageLink
            page={page + 1}
            disabled={page >= result.totalPages}
            lowStockOnly={lowStockOnly}
            search={search}
            label="Suivant →"
          />
        </div>
      )}
    </div>
  );
}

function StockRow({
  row,
  onSubmit,
  disabled,
}: {
  row: AdminInventoryRow;
  onSubmit: (event: FormEvent<HTMLFormElement>, row: AdminInventoryRow) => void;
  disabled: boolean;
}) {
  return (
    <tr className={["border-b border-line last:border-0", row.isLowStock ? "bg-red-50" : ""].join(" ")}>
      <td className="px-4 py-3">
        <p className="font-medium text-ink">{row.productName}</p>
        {row.nameSuffix && <p className="text-xs text-muted">{row.nameSuffix}</p>}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-muted">{row.sku}</td>
      <td className="px-4 py-3 text-ink">{row.warehouseName}</td>
      <td className="px-4 py-3 text-right text-ink">{row.quantityOnHand}</td>
      <td className="px-4 py-3 text-right text-ink">{row.quantityReserved}</td>
      <td className={["px-4 py-3 text-right font-medium", row.isLowStock ? "text-red-700" : "text-ink"].join(" ")}>
        {row.quantityAvailable}
      </td>
      <td className="px-4 py-3 text-right text-muted">{row.lowStockThreshold}</td>
      <td className="px-4 py-3">
        <form onSubmit={(event) => onSubmit(event, row)} className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            name="quantityDelta"
            step={1}
            required
            placeholder="±"
            title="Delta : positif pour un réassort, négatif pour une perte/correction"
            className="w-20 rounded-sm border border-line px-2 py-1 text-sm text-ink"
          />
          <input
            type="text"
            name="reason"
            required
            placeholder="Motif"
            className="w-40 rounded-sm border border-line px-2 py-1 text-sm text-ink"
          />
          <button
            type="submit"
            disabled={disabled}
            className="rounded-sm border border-navy px-3 py-1 text-xs font-medium text-navy hover:bg-navy hover:text-cream disabled:cursor-not-allowed disabled:opacity-50"
          >
            Ajuster
          </button>
        </form>
      </td>
    </tr>
  );
}

function PageLink({
  page,
  disabled,
  lowStockOnly,
  search,
  label,
}: {
  page: number;
  disabled: boolean;
  lowStockOnly: boolean;
  search: string;
  label: string;
}) {
  if (disabled) {
    return <span className="rounded-sm border border-line px-3 py-1.5 text-muted">{label}</span>;
  }

  const params = new URLSearchParams();
  params.set("page", String(page));
  if (lowStockOnly) params.set("lowStockOnly", "true");
  if (search) params.set("search", search);

  return (
    <Link to={`/admin/stock?${params.toString()}`} className="rounded-sm border border-navy px-3 py-1.5 text-navy hover:bg-navy hover:text-cream">
      {label}
    </Link>
  );
}
